import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { Avatar } from "@whoosh/design-system/src/components/display";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";

import { currentBookingAtom } from "../../../../atoms/stores";
import { PatronEditBooking } from "../../../../types";
import { AddNewPlayersHeaderNode$key } from "./__generated__/AddNewPlayersHeaderNode.graphql";

type Props = {
  nodeRef: AddNewPlayersHeaderNode$key | null;
  isGolfCourse: boolean;
  isRequestBooking: boolean;
  isLesson: boolean;
  selectedPlayers: PatronEditBooking.RoundPlayer[];
};

export const AddNewPlayersHeader = ({
  nodeRef,
  isGolfCourse,
  isRequestBooking,
  isLesson,
  selectedPlayers,
}: Props) => {
  const node = useFragment(AddNewPlayersHeaderNodeFragmentString, nodeRef);
  const currentBooking = useAtomValue(currentBookingAtom);
  // Get all rounds from all bookings that are NOT the current booking (currentBooking)
  const otherBookingRounds =
    node?.bookings?.edges
      ?.filter((edge) => edge?.node?.id !== currentBooking?.id)
      ?.flatMap(
        (edge) =>
          edge?.node?.rounds?.edges?.map((roundEdge) => roundEdge?.node) ?? []
      ) ?? [];

  // Get capacity information
  const groupSize = node?.standardCapacity ?? 4;
  const otherPlayerCapacity = !node?.requirePlayerCounts
    ? 0
    : (groupSize || 1) - (selectedPlayers.length + otherBookingRounds.length);

  // Determine if we should show a condensed view of available players
  const showCondensedView = groupSize > 6 && node?.requirePlayerCounts;

  return (
    <HStack
      py={4}
      px={6}
      alignItems="center"
      justifyContent="flex-start"
      gap={2}
      borderBottomWidth={1}
      borderColor="outline-default"
      bg="surface-primary"
      flexWrap="wrap"
    >
      {showCondensedView ? (
        <HStack alignItems="center" gap={2}>
          <Avatar size={"sm"} type={"unknownCustomer"} />
          <Text variant="mainBaseSemiBold">
            {otherPlayerCapacity}{" "}
            {otherPlayerCapacity === 1 ? t`Available spot` : t`Available spots`}
          </Text>
        </HStack>
      ) : (
        <>
          {selectedPlayers.map((player, index) => (
            <Avatar
              key={`${player.clientType}-${player.id ?? "empty"}-${index}`}
              profileImagePublicId={
                player.clientType !== "unknownGuest" &&
                player.clientType !== "unknownCustomer" &&
                player.clientType !== "memberHold"
                  ? player.profileImagePublicId ?? undefined
                  : undefined
              }
              firstName={player.firstName ?? null}
              lastName={player.lastName ?? null}
              size={"sm"}
              type={player.clientType}
            />
          ))}
          {Array.from({ length: otherPlayerCapacity }).map((_, index) => (
            <Avatar
              key={index}
              size={"sm"}
              type={
                isGolfCourse || isRequestBooking || isLesson
                  ? "empty"
                  : "member"
              }
            />
          ))}
        </>
      )}
    </HStack>
  );
};

export const AddNewPlayersHeaderNodeFragmentString = graphql`
  fragment AddNewPlayersHeaderNode on Node {
    ... on TimeSlot {
      requirePlayerCounts
      standardCapacity: capacity(bookingType: STANDARD)
      bookings {
        edges {
          node {
            id
            rounds {
              edges {
                node {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;
